import { yupResolver } from '@hookform/resolvers/yup';
import { QrCodeTwoTone } from '@mui/icons-material';
import { Button } from '@mui/material';
import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IRegistrationForm, RegistrationInputs, useSelectedOrganisation, Widget } from '../../../shared';
import { registrationFromFormMapper, registrationToFormMapper } from '../../../shared/mappers';
import { useCreateRegistration } from '../../hooks';
import { ISupportCaseInfo } from '../../models';
import { useRegistrationSchema } from '../../validators';
import { useGetSortedCustomAttributeConfigurations } from '../../../shared/utils/get-sorted-custom-attribute-configurations.util';
import { useCustomAttributeConfigurationList } from '../../../admin/hooks/custom-attributes-configuration.hook';

interface Props {
    supportCase: ISupportCaseInfo;
}

export const RegistrationForm: FC<Props> = ({ supportCase }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { organisation } = useSelectedOrganisation();

    const { mutateAsync: createRegistration, isPending: isCreatingRegistration } = useCreateRegistration();
    const { data: customAttributeConfigurations } = useCustomAttributeConfigurationList({
        organisationId: organisation?.id,
        pageSize: 100,
    });
    const sortedConfigurations = useGetSortedCustomAttributeConfigurations(customAttributeConfigurations?.data);

    const form = useForm<IRegistrationForm>({
        resolver: yupResolver(useRegistrationSchema()),
    });

    const onSubmit = async (registration: IRegistrationForm) => {
        if (supportCase && organisation?.id && registration.supportType.id && customAttributeConfigurations) {
            await createRegistration({
                supportCase: supportCase.supportCaseId,
                registration: registrationFromFormMapper(
                    registration,
                    organisation?.id,
                    customAttributeConfigurations.data,
                ),
            });
            navigate('/registration');
        }
    };

    useEffect(() => {
        form.reset(registrationToFormMapper(organisation, sortedConfigurations));
    }, [organisation, form, sortedConfigurations]);

    const actions = [
        <Button
            variant="contained"
            type="submit"
            disabled={organisation?.supportTypes?.length == 0 || isCreatingRegistration}
            sx={{ p: 1, height: '35px' }}
        >
            {t('register')}
        </Button>,
        <Button variant="text" onClick={() => navigate('/registration')} sx={{ p: 1, height: '35px' }}>
            {t('cancel')}
        </Button>,
    ];

    const reversedActions = [...actions].reverse();

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Widget
                    actions={reversedActions}
                    icon={<QrCodeTwoTone color="primary" />}
                    title={t('registration')}
                    footer={actions}
                >
                    <RegistrationInputs customAttributeConfigurations={sortedConfigurations} />
                </Widget>
            </form>
        </FormProvider>
    );
};
